import { of, Subject } from 'rxjs';
import { debounceTime, filter, switchMap, takeUntil } from 'rxjs/operators';
import { AvatarComponent } from 'src/app/_shared/components/avatar/avatar.component';
import { AccountSearchResult, AccountV2Service } from 'src/app/services/account-v2.service';

import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';

@Component({
  selector: 'app-select-account',
  templateUrl: './select-account.component.html',
  styleUrls: ['./select-account.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, AvatarComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectAccountComponent),
      multi: true,
    },
  ],
})
export class SelectAccountComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() placeholder = 'Buscar por nome ou e-mail da conta...';

  searchCtrl = new FormControl('');
  results: AccountSearchResult[] = [];
  selectedAccount: AccountSearchResult | null = null;
  isOpen = false;
  isDisabled = false;
  isLoading = false;

  private onChange: (value: AccountSearchResult | null) => void = () => {};
  private onTouched: () => void = () => {};
  private destroy$ = new Subject<void>();

  constructor(
    private readonly accountService: AccountV2Service,
    private readonly el: ElementRef<HTMLElement>
  ) {}

  ngOnInit(): void {
    this.searchCtrl.valueChanges
      .pipe(
        debounceTime(300),
        filter((value) => typeof value === 'string'),
        switchMap((value) => {
          const term = (value ?? '').trim();
          if (!term) {
            this.isLoading = false;
            return of([]);
          }
          this.isLoading = true;
          return this.accountService.searchAccounts({ name: term, email: term, document: term });
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((results) => {
        this.isLoading = false;
        this.results = results;
      });
  }

  onFocus(): void {
    if (!this.isDisabled) {
      this.isOpen = true;
    }
  }

  onSelect(account: AccountSearchResult): void {
    this.selectedAccount = account;
    this.isOpen = false;
    this.results = [];
    this.onChange(account);
    this.onTouched();
  }

  clearSelection(): void {
    this.selectedAccount = null;
    this.searchCtrl.setValue('', { emitEvent: false });
    this.onChange(null);
    this.onTouched();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.el.nativeElement.contains(event.target as Node)) {
      this.isOpen = false;
    }
  }

  writeValue(account: AccountSearchResult | null): void {
    this.selectedAccount = account;
  }

  registerOnChange(fn: (value: AccountSearchResult | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    isDisabled ? this.searchCtrl.disable() : this.searchCtrl.enable();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
